import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getTourDetailFields,
  getTourPageIndex,
  getTourPages,
} from "../data/pageGuides";

const PageTourContext = createContext(null);

const readStoredUser = () => {
  try {
    const raw = sessionStorage.getItem("userData") || localStorage.getItem("userData");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const stepIsAvailable = (step) => {
  if (!step?.fieldId) return true;
  return Boolean(
    document.querySelector(`[data-field-tour="${step.key}:${step.fieldId}"]`)
  );
};

export const usePageTour = () => {
  const context = useContext(PageTourContext);
  if (!context) {
    throw new Error("usePageTour must be used within a PageTourProvider");
  }
  return context;
};

export function PageTourProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [tourIndex, setTourIndex] = useState(0);
  const [detailIndex, setDetailIndex] = useState(-1);
  const [user, setUser] = useState(readStoredUser);

  const tourPages = useMemo(() => getTourPages(user), [user]);
  const guide = active ? tourPages[tourIndex] || null : null;
  const details = useMemo(
    () => (guide ? getTourDetailFields(guide.key) : []),
    [guide]
  );
  const detail = detailIndex >= 0 ? details[detailIndex] || null : null;

  const stopTour = useCallback(() => {
    setActive(false);
    setTourIndex(0);
    setDetailIndex(-1);
  }, []);

  const startTour = useCallback(() => {
    const nextUser = readStoredUser();
    const pages = getTourPages(nextUser);
    const currentIndex = getTourPageIndex(location.pathname, nextUser);
    const startIndex = currentIndex >= 0 ? currentIndex : 0;
    setUser(nextUser);
    setDetailIndex(-1);
    setTourIndex(startIndex);
    setActive(true);
    const startGuide = pages[startIndex];
    if (!startGuide) return;
    const alreadyHere =
      location.pathname === startGuide.path ||
      (startGuide.matchNested &&
        location.pathname.startsWith(`${startGuide.path}/`));
    if (alreadyHere) return;
    const nextPath = startGuide.openPath || startGuide.path;
    if (nextPath && nextPath !== location.pathname) {
      navigate(nextPath);
    }
  }, [location.pathname, navigate]);

  const goToTourPage = useCallback(
    (index, direction = 1) => {
      let nextIndex = index;
      while (
        nextIndex >= 0 &&
        nextIndex < tourPages.length &&
        !stepIsAvailable(tourPages[nextIndex])
      ) {
        nextIndex += direction;
      }
      if (nextIndex < 0) return;
      if (nextIndex >= tourPages.length) {
        setActive(false);
        setTourIndex(0);
        setDetailIndex(-1);
        return;
      }
      const nextGuide = tourPages[nextIndex];
      setDetailIndex(-1);
      setTourIndex(nextIndex);
      const nextPath = nextGuide.openPath || nextGuide.path;
      if (nextPath !== location.pathname) {
        navigate(nextPath);
      }
    },
    [location.pathname, navigate, tourPages]
  );

  const nextPage = useCallback(() => {
    if (tourIndex >= tourPages.length - 1) {
      setActive(false);
      setTourIndex(0);
      setDetailIndex(-1);
      return;
    }
    goToTourPage(tourIndex + 1, 1);
  }, [goToTourPage, tourIndex, tourPages.length]);

  const prevPage = useCallback(() => {
    if (tourIndex <= 0) return;
    goToTourPage(tourIndex - 1, -1);
  }, [goToTourPage, tourIndex]);

  const openDetails = useCallback(() => {
    if (!details.length) return;
    setDetailIndex(0);
  }, [details.length]);

  const closeDetails = useCallback(() => {
    setDetailIndex(-1);
  }, []);

  const nextDetail = useCallback(() => {
    if (detailIndex >= details.length - 1) {
      setDetailIndex(-1);
      return;
    }
    setDetailIndex(detailIndex + 1);
  }, [detailIndex, details.length]);

  const prevDetail = useCallback(() => {
    if (detailIndex <= 0) {
      setDetailIndex(-1);
      return;
    }
    setDetailIndex(detailIndex - 1);
  }, [detailIndex]);

  const value = useMemo(
    () => ({
      active,
      guide,
      detail,
      details,
      detailsOpen: Boolean(detail),
      hasDetails: details.length > 0,
      pageIndex: tourIndex,
      totalPages: tourPages.length,
      detailIndex,
      totalDetails: details.length,
      isFirstPage: tourIndex <= 0,
      isLastPage: tourIndex >= tourPages.length - 1,
      isFirstDetail: detailIndex <= 0,
      isLastDetail: detailIndex >= details.length - 1,
      startTour,
      stopTour,
      nextPage,
      prevPage,
      openDetails,
      closeDetails,
      nextDetail,
      prevDetail,
    }),
    [
      active,
      guide,
      detail,
      details,
      tourIndex,
      tourPages.length,
      detailIndex,
      startTour,
      stopTour,
      nextPage,
      prevPage,
      openDetails,
      closeDetails,
      nextDetail,
      prevDetail,
    ]
  );

  return (
    <PageTourContext.Provider value={value}>{children}</PageTourContext.Provider>
  );
}
